import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { Box, Chip, Button as MuiButton, Typography, makeStyles, useTheme, withStyles } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import { Add, Refresh, Remove } from '@material-ui/icons';
import clsx from 'clsx';
import MuiDialog from '../../../base/components/mui/MuiDialog';
import { SelectInput, TextInput } from '../../../base/components/ra/inputs';
import AmountInput from '../../../base/components/ra/inputs/AmountInput';
import { useUpdateAdjustment } from '../hooks';
import { formatMoney } from '../../../services/util/formatNumber';
import LoadingOverlay from '../../../base/components/layout/LoadingOverlay';

const UpdateAdjustmentModal = ({
  trigger,
  invoiceNo,
  invoiceId,
  amount,
  description,
  onSuccess,
  currencyCode,
  totalInvoiceAmount,
}) => {
  const t = useTranslate();
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [reset, setReset] = useState(false);

  const { updateAdjustment, isUpdating } = useUpdateAdjustment({
    onSuccess: () => {
      onSuccess?.();
      setOpen(false);
    },
  });

  const handleUpdateAdjustment = (values) => {
    updateAdjustment({
      invoiceId,
      description: values.description.trim() || '',
      amount:
        values.adjustmentType === ADJUSTMENT_TYPE.INCREASE
          ? Math.abs(values.adjustmentAmount)
          : -Math.abs(values.adjustmentAmount),
    });
  };

  const handleValidation = (values) => {
    const errors = {};
    if (!values.description?.trim?.()) {
      errors.description = t('ra.validation.required');
    }
    if (values.description && values.description.trim().length > 30) {
      errors.description = t('ra.validation.maxLength', {
        max: 30,
      });
    }

    if (values.adjustmentAmount == null || Number.isNaN(values.adjustmentAmount)) {
      errors.adjustmentAmount = t('ra.validation.required');
    }

    if (
      values.adjustmentAmount &&
      values.adjustmentType === ADJUSTMENT_TYPE.DECREASE &&
      Math.abs(values.adjustmentAmount) > (totalInvoiceAmount || 0)
    ) {
      errors.adjustmentAmount = t('ra.validation.maxValue', {
        max: totalInvoiceAmount || 0,
      });
    }

    if (
      values.adjustmentAmount &&
      values.adjustmentType === ADJUSTMENT_TYPE.INCREASE &&
      totalInvoiceAmount < 0 &&
      Math.abs(totalInvoiceAmount) > Math.abs(values.adjustmentAmount)
    ) {
      errors.adjustmentAmount = t('ra.validation.minValue', {
        min: Math.abs(totalInvoiceAmount),
      });
    }

    if (values.adjustmentAmount && values.adjustmentType === ADJUSTMENT_TYPE.DECREASE && totalInvoiceAmount <= 0) {
      errors.adjustmentAmount = t('resources.invoice.validation.disabled-downward-adjustment');
    }

    return errors;
  };

  const handleResetAdjustment = () => {
    updateAdjustment({
      invoiceId,
      amount: 0,
      description: '',
    });
  };

  useEffect(() => {
    if (!open) {
      setReset(false);
    }
  }, [open, setReset]);

  return (
    <>
      <LoadingOverlay isLoading={isUpdating} loadingMessage={t('ra.message.updating')} />
      {React.isValidElement(trigger) &&
        React.cloneElement(trigger, {
          onClick: () => {
            setOpen(true);
          },
        })}
      <MuiDialog
        open={open}
        title={t('resources.invoice.adjustInvoice')}
        description={t('resources.invoice.adjustInvoiceDesc', {
          invoiceNo,
        })}
        onClose={() => setOpen(false)}
        classes={{
          paper: classes.paper,
        }}
      >
        {reset ? (
          <>
            <Box className={classes.resetBanner}>
              <Typography variant="h4">{t('resources.invoice.resetAdjustmentTitle')}</Typography>
              <Typography>{t('resources.invoice.resetAdjustmentMessage')}</Typography>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              mt={4}
              sx={{
                gap: 8,
              }}
            >
              <Button variant="outlined" disabled={isUpdating} className="ra-confirm" onClick={() => setReset(false)}>
                {t('ra.action.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={isUpdating}
                className={clsx('ra-confirm', classes.containedNegativeButton)}
                disableElevation
                onClick={handleResetAdjustment}
              >
                {t('resources.invoice.confirmReset')}
              </Button>
            </Box>
          </>
        ) : (
          <Form
            onSubmit={handleUpdateAdjustment}
            validate={handleValidation}
            initialValues={{
              adjustmentAmount: typeof amount === 'number' ? Math.abs(amount) : undefined,
              description: description || '',
              adjustmentType: amount < 0 ? ADJUSTMENT_TYPE.DECREASE : ADJUSTMENT_TYPE.INCREASE,
            }}
            render={({ handleSubmit, form }) => {
              const adjusmentType = form.getFieldState('adjustmentType')?.value;
              const adjustmentAmount = form.getFieldState('adjustmentAmount')?.value || 0;
              const adjustment =
                adjusmentType === ADJUSTMENT_TYPE.DECREASE ? -Math.abs(adjustmentAmount) : Math.abs(adjustmentAmount);

              return (
                <form onSubmit={handleSubmit}>
                  <InvoiceAmount
                    label={t('resources.invoice.currentAmount')}
                    amount={totalInvoiceAmount - (amount || 0)}
                    currencyCode={currencyCode}
                  />
                  <Box
                    display="grid"
                    gridTemplateColumns="1fr 2fr"
                    sx={{
                      gap: 8,
                    }}
                    mt={4}
                  >
                    <SelectInput
                      label={t('resources.invoice.fields.adjustmentType')}
                      source="adjustmentType"
                      choices={Object.values(ADJUSTMENT_TYPE).map((item) => ({
                        id: item,
                        disabled: item === ADJUSTMENT_TYPE.DECREASE && totalInvoiceAmount <= 0,
                        name: (
                          <Box
                            display="flex"
                            alignItems="center"
                            color={
                              item === ADJUSTMENT_TYPE.DECREASE ? theme.palette.error.main : theme.palette.success.main
                            }
                            sx={{
                              gap: 4,
                            }}
                          >
                            {item === ADJUSTMENT_TYPE.DECREASE ? (
                              <Remove className={classes.adjustmentTypeIcon} />
                            ) : (
                              <Add className={classes.adjustmentTypeIcon} />
                            )}
                            <Typography className={classes.adjustmentTypeLabel}>
                              {t(`resources.invoice.adjustmentType.${item}`)}
                            </Typography>
                          </Box>
                        ),
                      }))}
                      resettable={false}
                    />
                    <AmountInput
                      source="adjustmentAmount"
                      label={t('resources.invoice.fields.adjustmentAmount')}
                      placeholder={t('resources.invoice.fields.adjustmentAmountPlaceholder')}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      suffix={currencyCode ? ` ${currencyCode}` : ''}
                      allowNegative
                      onValueChange={(value) => {
                        const adjustmentType = form.getFieldState('adjustmentType')?.value;
                        if (value < 0 && adjustmentType === ADJUSTMENT_TYPE.INCREASE) {
                          form.change('adjustmentType', ADJUSTMENT_TYPE.DECREASE);
                        }
                      }}
                    />
                  </Box>

                  <TextInput
                    source="description"
                    label={t('resources.invoice.fields.description')}
                    placeholder={t('resources.invoice.fields.descriptionPlaceholder')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  {!!adjustment && !!Math.max(totalInvoiceAmount - (amount || 0) + adjustment, 0) && (
                    <InvoiceAmount
                      label={t('resources.invoice.amountAfterAdjustment')}
                      highlight
                      amount={totalInvoiceAmount - (amount || 0) + adjustment}
                      adjustment={adjustment}
                      currencyCode={currencyCode}
                    />
                  )}

                  <Box
                    mt={4}
                    display="flex"
                    width="100%"
                    justifyContent="justify-between"
                    sx={{
                      gap: 8,
                    }}
                  >
                    {!!amount && (
                      <Button
                        variant="outlined"
                        disabled={isUpdating}
                        className={clsx('ra-confirm', classes.outlinedNegativeButton)}
                        startIcon={<Refresh fontSize="small" />}
                        onClick={() => setReset(true)}
                      >
                        {t('resources.invoice.reset')}
                      </Button>
                    )}
                    <Box
                      display="flex"
                      ml="auto"
                      sx={{
                        gap: 8,
                      }}
                    >
                      <Button
                        variant="outlined"
                        disabled={isUpdating}
                        className="ra-confirm"
                        onClick={() => setOpen(false)}
                      >
                        {t('ra.action.cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isUpdating || !form.getState().dirty}
                        className="ra-confirm"
                        disableElevation
                      >
                        {t('resources.invoice.applyAdjustment')}
                      </Button>
                    </Box>
                  </Box>
                </form>
              );
            }}
          />
        )}
      </MuiDialog>
    </>
  );
};

export default UpdateAdjustmentModal;

const ADJUSTMENT_TYPE = {
  INCREASE: 1,
  DECREASE: 2,
};

const InvoiceAmount = ({ highlight, amount, adjustment, label, currencyCode }) => {
  const classes = useStyles();
  return (
    <Box className={clsx(classes.invoiceAmountBox, highlight && classes.highlightInvoiceAmountBox)}>
      <Typography variant="body2" className={classes.invoiceBoxLabel}>
        {label}
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        sx={{
          gap: 8,
        }}
      >
        <Typography variant="h3">{formatMoney(amount, currencyCode)}</Typography>

        {!!adjustment && (
          <Chip
            variant="outlined"
            size="small"
            label={`${adjustment > 0 ? '+' : '-'}${formatMoney(Math.abs(adjustment), currencyCode)}`}
            className={adjustment < 0 ? classes.negativeChip : classes.positiveChip}
          />
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  outlinedNegativeButton: {
    color: theme.palette.error.dark,
    borderColor: theme.palette.error.dark,
    '&:hover': {
      background: theme.palette.error[100],
    },
  },
  containedNegativeButton: {
    background: theme.palette.error.dark,
    color: 'white',
    '&:hover': {
      background: theme.palette.error.main,
    },
  },
  paper: {
    maxWidth: 500,
  },
  resetBanner: {
    color: theme.palette.error.dark,
    border: `1px solid ${theme.palette.error.main}`,
    background: theme.palette.error[100],
    borderRadius: 8,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  adjustmentTypeIcon: {
    width: 16,
    height: 16,
  },
  adjustmentTypeLabel: {
    lineHeight: '14px',
    color: theme.palette.text.primary,
  },
  invoiceAmountBox: {
    borderRadius: 8,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    background: theme.palette.grey[100],
  },
  highlightInvoiceAmountBox: {
    border: `1px solid ${theme.palette.grey[800]}`,
  },
  invoiceBoxLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.grey[700],
  },
  negativeChip: {
    color: theme.palette.error.dark,
    border: `1px solid ${theme.palette.error.dark}`,
    backgroundColor: theme.palette.error[100],
    fontWeight: 600,
  },
  positiveChip: {
    color: theme.palette.success.dark,
    border: `1px solid ${theme.palette.success.dark}`,
    backgroundColor: theme.palette.success[100],
    fontWeight: 600,
  },
}));

const Button = withStyles({
  root: {
    margin: '0 !important',
  },
})(MuiButton);
